// types
import type {
  CustomerVehiclePayload,
  CustomerVehicleSaveSource,
  DecodedVehicle
} from '@/types/domain/customerVehicle'

/**
 * Vehicle text used to build a decoder input string.
 */
interface VehicleDecodeSource {
  make?: string | null
  model?: string | null
  derivative?: string | null
  modelYear?: number | string | null
}

/**
 * Labels shown for each decoded vehicle field.
 */
/**
 * Fields that must be present before a decoded vehicle can be saved.
 */
const requiredDecodedVehicleFields = ['make', 'model', 'trim'] as const

type RequiredDecodedVehicleField = (typeof requiredDecodedVehicleFields)[number]

const decodedVehicleFields: Array<{ key: keyof DecodedVehicle; label: string; required?: boolean }> = [
  { key: 'make', label: 'Make', required: true },
  { key: 'model', label: 'Model', required: true },
  { key: 'bodyStyle', label: 'Body style' },
  { key: 'trim', label: 'Trim', required: true },
  { key: 'derivative', label: 'Derivative' },
  { key: 'transmission', label: 'Transmission' },
  { key: 'transmissionBadge', label: 'Transmission badge' },
  { key: 'engineBadge', label: 'Engine badge' },
  { key: 'powerBadge', label: 'Power badge' },
  { key: 'lengthVariant', label: 'Length variant' },
  { key: 'fuelType', label: 'Fuel type' },
  { key: 'doors', label: 'Doors' },
  { key: 'modelYear', label: 'Model year' },
  { key: 'modelYearStart', label: 'Model year start' },
  { key: 'modelYearEnd', label: 'Model year end' },
  { key: 'engineDisplacement', label: 'Engine displacement' },
  { key: 'powerClass', label: 'Power class' },
  { key: 'powertrainVariant', label: 'Powertrain variant' },
  { key: 'engineGeneration', label: 'Engine generation' },
  { key: 'enginePs', label: 'Engine PS' },
  { key: 'engineKw', label: 'Engine kW' },
  { key: 'batteryCapacity', label: 'Battery capacity' },
  { key: 'aspiration', label: 'Aspiration' },
  { key: 'drivetrain', label: 'Drivetrain' },
  { key: 'drivetrainBadge', label: 'Drivetrain badge' },
  { key: 'gears', label: 'Gears' },
  { key: 'seats', label: 'Seats' },
  { key: 'tags', label: 'Tags' }
]

/**
 * Builds the full vehicle string sent to the decoder.
 * Model year is appended only when it is not already present.
 */
function buildVehicleDecodeString(source: VehicleDecodeSource): string {
  const parts = [source.make, source.model, source.derivative]
    .map(part => (part ?? '').trim())
    .filter(part => part !== '')

  let vehicleString = parts.join(' ').replace(/\s+/g, ' ').trim()
  const modelYear = source.modelYear == null ? '' : String(source.modelYear).trim()

  // Keep a separate model year when the description does not already contain it.
  if (modelYear !== '' && !vehicleString.includes(modelYear)) {
    vehicleString = `${vehicleString} ${modelYear}`.trim()
  }

  return vehicleString
}

/**
 * Uses the decoded value, then the agreement value, when the decoded value is empty.
 */
function preferDecodedValue<T>(decoded: T | null | undefined, fallback: T | null | undefined): T | null {
  if (decoded !== null && decoded !== undefined && decoded !== '') {
    return decoded
  }

  if (fallback !== null && fallback !== undefined && fallback !== '') {
    return fallback
  }

  return null
}

/**
 * Builds the customer vehicle create payload from a confirmed decode.
 */
function buildCustomerVehiclePayload(source: CustomerVehicleSaveSource): CustomerVehiclePayload {
  const decoded = source.decoded

  return {
    agreementId: source.agreementId,
    customerId: source.customerId,
    sourceDescription: source.sourceDescription,
    vin: (source.vin ?? '').replace(/\s+/g, '').toUpperCase(),
    vrm: source.vrm,
    newUsed: source.newUsed,
    make: preferDecodedValue(decoded.make, source.fallback.make),
    model: preferDecodedValue(decoded.model, source.fallback.model),
    derivative: preferDecodedValue(decoded.derivative, source.fallback.derivative),
    modelYear: preferDecodedValue(decoded.modelYear, source.fallback.modelYear),
    isVehicleImported: source.isVehicleImported,
    vehicleCategory: source.vehicleCategory,
    doors: preferDecodedValue(decoded.doors, source.fallback.doors),
    seats: decoded.seats,
    bodyStyle: decoded.bodyStyle,
    transmission: decoded.transmission,
    fuelType: preferDecodedValue(decoded.fuelType, source.fallback.fuelType),
    color: source.color,
    previousOwners: source.previousOwners,
    registrationDate: source.registrationDate,
    trim: decoded.trim,
    engineBadge: decoded.engineBadge,
    engineDisplacement: decoded.engineDisplacement,
    engineGeneration: decoded.engineGeneration,
    powerBadge: decoded.powerBadge,
    powerClass: decoded.powerClass,
    powertrainVariant: decoded.powertrainVariant,
    enginePs: decoded.enginePs,
    engineKw: decoded.engineKw,
    batteryCapacity: decoded.batteryCapacity,
    aspiration: decoded.aspiration,
    transmissionBadge: decoded.transmissionBadge,
    gears: decoded.gears,
    drivetrain: decoded.drivetrain,
    drivetrainBadge: decoded.drivetrainBadge,
    modelYearStart: decoded.modelYearStart,
    modelYearEnd: decoded.modelYearEnd,
    lengthVariant: decoded.lengthVariant,
    tags: decoded.tags
  }
}

/**
 * Whether a decoded value is empty.
 */
function isEmptyDecodedValue(value: DecodedVehicle[keyof DecodedVehicle]): boolean {
  if (value == null || value === '') {
    return true
  }

  if (Array.isArray(value)) {
    return value.length === 0
  }

  return String(value).trim() === ''
}

/**
 * Required fields that the decode did not fill.
 */
function missingRequiredDecodedFields(decoded: DecodedVehicle | null): RequiredDecodedVehicleField[] {
  if (!decoded) {
    return [...requiredDecodedVehicleFields]
  }

  return requiredDecodedVehicleFields.filter(field => isEmptyDecodedValue(decoded[field]))
}

/**
 * Turns decoded field keys into a readable list, such as "Make, Model and Trim".
 */
function formatMissingDecodedFields(fields: string[]): string {
  const labels = fields.map(field => {
    return decodedVehicleFields.find(item => item.key === field)?.label ?? field
  })

  if (labels.length <= 1) {
    return labels[0] ?? ''
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Formats one decoded value for display.
 */
function formatDecodedVehicleValue(value: DecodedVehicle[keyof DecodedVehicle]): string {
  if (value == null || value === '') {
    return '—'
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '—'
  }

  return String(value)
}

export {
  buildCustomerVehiclePayload,
  buildVehicleDecodeString,
  decodedVehicleFields,
  formatDecodedVehicleValue,
  formatMissingDecodedFields,
  missingRequiredDecodedFields
}
export type { RequiredDecodedVehicleField, VehicleDecodeSource }
